<template>
    <v-app :class="{ native: isNative }">
        <audio :id="AUDIO_ASSET_ID"
               ref="bg-audio"
               :src="`/${SoundManager.path(AUDIO_ASSET_ID)}`"
               preload
               loop
        />
        <router-view v-if="showRouterView" />
        <login-rewards-modal v-if="hasLoginRewards"
                             :show="hasLoginRewards"
        />
        <release-notes-modal v-if="hasReleaseNotes"
                             :show="hasReleaseNotes"
        />
        <bout-invite-received-modal />
        <system-broadcast-modal />
        <v-snackbar v-model="achievementToast"
                    color="amber-darken-2"
                    :timeout="5000"
                    location="bottom"
                    @click="onAchievementToastClick"
        >
            <div class="d-flex align-center">
                <v-icon icon="mdi-trophy" class="mr-2" />
                <div>
                    <div class="font-weight-bold">Achievement Unlocked!</div>
                    <div class="text-caption">{{ achievementToastMessage }}</div>
                </div>
            </div>
            <template v-slot:actions>
                <v-btn icon="mdi-close" size="small" variant="text" @click.stop="achievementToast = false" />
            </template>
        </v-snackbar>
        <v-snackbar v-model="errorToast"
                    color="error"
                    :timeout="5000"
                    location="bottom"
        >
            <div class="d-flex align-center">
                <v-icon icon="mdi-alert-circle" class="mr-2" />
                <div>
                    <div class="font-weight-bold">{{ errorToastHeader }}</div>
                    <div class="text-caption">{{ errorToastMessage }}</div>
                </div>
            </div>
        </v-snackbar>
        <v-snackbar v-model="pushToast"
                    color="primary"
                    :timeout="5000"
                    location="top"
                    @click="onPushToastClick"
        >
            <div class="d-flex align-center">
                <v-icon icon="mdi-bell" class="mr-2" />
                <div>
                    <div class="font-weight-bold">{{ pushToastTitle }}</div>
                    <div class="text-caption">{{ pushToastBody }}</div>
                </div>
            </div>
            <template v-slot:actions>
                <v-btn icon="mdi-close" size="small" variant="text" @click.stop="pushToast = false" />
            </template>
        </v-snackbar>
        <div v-if="updateDownloading" class="update-overlay">
            <div class="update-overlay__content">
                <v-progress-circular indeterminate size="48" width="4" color="white" />
                <div class="update-overlay__title">Downloading update…</div>
                <div class="update-overlay__subtitle">
                    Please wait while the latest version is downloaded and installed.
                </div>
                <div v-if="updateProgress > 0" class="update-overlay__percent">
                    {{ Math.round(updateProgress) }}%
                </div>
            </div>
        </div>
        <div v-if="showConnectingOverlay" class="update-overlay">
            <div class="update-overlay__content">
                <v-progress-circular indeterminate size="48" width="4" color="white" />
                <div class="update-overlay__title">Connecting…</div>
            </div>
        </div>
    </v-app>
</template>

<script setup>
    import {computed, ref, watch, onMounted, onBeforeUnmount, useTemplateRef} from "vue";
    import EventBus from "@/services/eventBus";
    import StorageManager from "@/services/storageManager";
    import SoundManager from "@/services/soundManager";
    import PushManager from "@/services/push-manager";
    import {storeToRefs} from "pinia";
    import {useMainStore} from "@/store";
    import {useRoute, useRouter} from "vue-router";
    import {Capacitor, SystemBars} from "@capacitor/core";
    import {App as CapacitorApp} from "@capacitor/app";
    import {KeepAwake} from "@capacitor-community/keep-awake";
    import {SplashScreen} from "@capacitor/splash-screen";
    import EchoManager from "@/services/echo-manager";
    import LoginRewardsModal from "@/components/Modals/LoginRewardsModal";
    import BoutInviteReceivedModal from "@/components/Modals/BoutInviteReceivedModal";
    import ReleaseNotesModal from "@/components/Modals/ReleaseNotesModal";
    import SystemBroadcastModal from "@/components/Modals/SystemBroadcastModal";
    import {
        LIVE_UPDATE_DOWNLOADING_EVENT,
        LIVE_UPDATE_PROGRESS_EVENT,
        LIVE_UPDATE_DISMISS_EVENT,
    } from "@/services/liveUpdates";

    const isNative = Capacitor.isNativePlatform();

    const AUDIO_ASSET_ID = 'bg-audio';

    const store = useMainStore();
    const route = useRoute();
    const router = useRouter();
    const {isProduction, loggedIn, connected} = storeToRefs(store);
    const {login_rewards, release_notes, references, volume} = storeToRefs(store);

    const bgAudio = useTemplateRef('bg-audio');

    const loading = computed(() => references.value.loading);
    const loaded = computed(() => references.value.loaded);
    // Routes that never load references (logged-out / guest area: register,
    // login, vpn-required, maintenance, banned, masquerade all live under
    // /auth). On these screens references.loaded never becomes true, so the
    // reference-gated splash hide below never fires. On a fresh native install
    // the user lands here first, which is why the app sat on the splash icon
    // forever. Hiding on these routes is safe: nothing on a guest screen reads
    // references.data, so there is no un-populated-app flash to guard against.
    // Authenticated routes are unaffected — they still wait for `loaded`.
    const isGuestRoute = computed(() => route.path.startsWith('/auth'));
    // Gate for the <router-view>. Previously this was `! loading`, which flips
    // on EVERY reference fetch — including the in-session ReferenceFile
    // websocket update — tearing down and remounting the current page (a visible
    // "reload"). Instead, show the view once the first reference load completes
    // (`loaded`, set once in addToStore and never reset) or on guest routes
    // (which never load references). Subsequent reference refreshes keep `loaded`
    // true, so the view stays mounted and the data swap is applied reactively.
    const showRouterView = computed(() => loaded.value || isGuestRoute.value);
    // The loading screen's image (shared with the live-update overlay's
    // background) should stay up through login and the websocket handshake,
    // not just through reference loading. On authenticated routes, that means
    // waiting on both `loggedIn` and the Echo/Reverb `connected` state; guest
    // routes never log in or connect, so they are excluded.
    const awaitingConnection = computed(() => !isGuestRoute.value && !(loggedIn.value && connected.value));
    // Show the "Connecting…" overlay from the moment this component mounts
    // (rather than waiting on `loaded`) so it is already on screen the instant
    // the raw-DOM "Checking for updates…" overlay (liveUpdates.js) is torn down
    // by main.js's mount handoff. Waiting on `loaded` left a gap between the two
    // overlays — on authenticated routes `showRouterView` also stays false until
    // `loaded`, so there is nothing meaningful to reveal underneath in the
    // meantime. Guest routes are still excluded via `awaitingConnection`.
    const showConnectingOverlay = computed(() => awaitingConnection.value && !updateDownloading.value);
    const refError = computed(() => references.value.error);
    const musicVolume = computed(() => volume.value.music);
    const hasLoginRewards = computed(() => login_rewards.value.length > 0);
    const hasReleaseNotes = computed(() => release_notes.value !== null);

    const achievementToast = ref(false);
    const achievementToastMessage = ref('');
    const achievementToastAchievementId = ref(null);
    const achievementToastQueue = ref([]);
    const achievementToastsPaused = ref(false);

    const errorToast = ref(false);
    const errorToastHeader = ref('');
    const errorToastMessage = ref('');

    const pushToast = ref(false);
    const pushToastTitle = ref('');
    const pushToastBody = ref('');
    const pushToastData = ref(null);

    // Full-screen, interaction-blocking overlay shown while a live update
    // downloads. Auto-apply then hands off to the raw-DOM "Updating..." overlay
    // (see liveUpdates.promoteAndReload) which persists through the reload.
    const updateDownloading = ref(false);
    const updateProgress = ref(0);

    // Handle to the native app-state listener so it can be removed on unmount.
    let appStateListener = null;

    function onPushToastClick() {
        pushToast.value = false;
        if (pushToastData.value?.deep_link) {
            PushManager.navigateToDeepLink(pushToastData.value.deep_link);
        }
    }
    function showNextAchievementToast() {
        if (achievementToastsPaused.value || achievementToastQueue.value.length === 0) {
            return;
        }
        const next = achievementToastQueue.value.shift();
        achievementToastMessage.value = next.message;
        achievementToastAchievementId.value = next.achievement_id;
        achievementToast.value = true;
    }

    function onAchievementToastClick() {
        achievementToast.value = false;
        const achievementId = achievementToastAchievementId.value;
        router.push({name: 'achievements'}).then(() => {
            if (achievementId !== null && achievementId !== undefined) {
                EventBus.emit('achievement-claim-open', achievementId);
            }
        });
    }

    function showError(message = null, header = null) {
        errorToastHeader.value = header === null ? 'Error Encountered' : header;
        errorToastMessage.value = message === null ? 'An error was encountered.' : message;
        errorToast.value = true;
    }
    async function initAudio() {
        SoundManager.register(AUDIO_ASSET_ID, bgAudio.value);
        await SoundManager.preload(AUDIO_ASSET_ID);
        SoundManager.setMusicVolume(AUDIO_ASSET_ID, musicVolume.value);
        await SoundManager.playMusic(AUDIO_ASSET_ID);
    }

    watch(loading, async (newVal) => {
        if (! newVal && isNative) {
            await SplashScreen.hide();
        }
    });
    watch(loaded, async (newVal) => {
        if (newVal && isNative) {
            await SplashScreen.hide();
        }
    });
    watch(refError, (newVal) => {
        if (newVal) {
            showError(isProduction.value ? null : 'A reference error was encountered.');
        }
    });
    watch(musicVolume, () => {
        SoundManager.setMusicVolume(AUDIO_ASSET_ID, musicVolume.value);
    });
    watch(achievementToast, (visible) => {
        if (!visible && achievementToastQueue.value.length > 0) {
            setTimeout(showNextAchievementToast, 300);
        }
    });
    watch(achievementToastsPaused, (paused) => {
        if (!paused && !achievementToast.value && achievementToastQueue.value.length > 0) {
            setTimeout(showNextAchievementToast, 300);
        }
    });
    watch(() => route.name, () => {
        //THIS ONLY RUNS ON WEBAPP. NATIVE AUDIO WILL ALREADY BE PLAYING AT THIS STAGE.
        SoundManager.playMusic(AUDIO_ASSET_ID);
    });
    // Suppress achievement "tier ready to claim" toasts for the whole duration
    // of a run (preview → bout → scoreboard → ad-break) so they do not pop
    // between bouts while the player is still progressing. Claimable tiers that
    // arrive during the run stay queued and only flush once the player leaves
    // these screens — notably onto the celebratory `division-complete` screen in
    // single player (or back to the lobby in other modes). This keeps the toast
    // from competing with the per-bout result and defers it to a natural beat.
    const RUN_ROUTES = ['preview', 'bout', 'scoreboard', 'ad-break'];
    watch(() => route.name, (name) => {
        achievementToastsPaused.value = RUN_ROUTES.includes(name);
    }, {immediate: true});
    // Hide the splash when navigation settles on a guest route (references are
    // not loaded on these screens, so the `loaded`/`loading` watchers never fire
    // for them). Covers the fresh-install redirect into /auth/register.
    watch(isGuestRoute, async (guest) => {
        if (guest && isNative) {
            await SplashScreen.hide();
        }
    });

    if (Object.prototype.hasOwnProperty.call(route.query, 'XDEBUG_SESSION_START')) {
        StorageManager.set('xdebug_session', route.query.XDEBUG_SESSION_START);
    }

    onMounted(async () => {
        if (isNative) {
            await SystemBars.hide();

            // Keep the screen awake while the app is in the foreground. The
            // initial foreground does not emit an appStateChange, so seed it
            // here; the listener below then tracks subsequent transitions.
            await KeepAwake.keepAwake();

            // When the app returns to the foreground, the OS may have silently
            // dropped the Echo websocket while backgrounded. Verify it's still
            // live and reconnect if not. Also toggle the keep-awake lock with
            // foreground state: hold it while active, release it on background
            // so power management returns to the OS while the user is elsewhere.
            appStateListener = await CapacitorApp.addListener('appStateChange', ({isActive}) => {
                if (isActive) {
                    EchoManager.ensureConnected();
                    KeepAwake.keepAwake();
                } else {
                    KeepAwake.allowSleep();
                }
            });
        }

        EventBus.on('error-fatal', message => {
            showError(isProduction.value ? null : 'A fatal error was encountered: ' + message);
        });
        EventBus.on('error-toast', message => {
            showError(isProduction.value ? null : message);
        });
        EventBus.on('error-auth', () => {
            showError('You are not authorized to view the requested content.', 'Authorization Error');
        });
        EventBus.on('toasts-pause', () => {
            achievementToastsPaused.value = true;
        });
        EventBus.on('toasts-resume', () => {
            achievementToastsPaused.value = false;
        });
        EventBus.on('achievement-progress', (data) => {
            if (!data || typeof data !== 'object') {
                return;
            }
            // Always update store with latest progress
            const existing = store.achievements.find(a => a.achievement_id === data.achievement_id);
            if (existing) {
                existing.progress = data.progress;
            } else {
                store.achievements.push({
                    achievement_id: data.achievement_id,
                    progress: data.progress,
                    tier_claimed: null,
                });
            }
            // Show toast only when a tier becomes claimable
            if (data.claimable && data.claimable_tier) {
                const message = data.name + ' — ' + data.claimable_tier + ' tier ready to claim!';
                achievementToastQueue.value.push({message, achievement_id: data.achievement_id});
                if (!achievementToastsPaused.value && !achievementToast.value) {
                    showNextAchievementToast();
                }
            }
        });
        EventBus.on('push-notification', (notification) => {
            pushToastTitle.value = notification.title;
            pushToastBody.value = notification.body;
            pushToastData.value = notification.data;
            pushToast.value = true;
        });
        EventBus.on(LIVE_UPDATE_DOWNLOADING_EVENT, (percent) => {
            updateProgress.value = typeof percent === 'number' ? percent : 0;
            updateDownloading.value = true;
        });
        EventBus.on(LIVE_UPDATE_PROGRESS_EVENT, (percent) => {
            if (typeof percent === 'number') {
                updateProgress.value = percent;
            }
        });
        EventBus.on(LIVE_UPDATE_DISMISS_EVENT, () => {
            updateDownloading.value = false;
            updateProgress.value = 0;
        });
        // References-loaded path (authenticated) OR guest route (no references
        // expected). The guest case is what unblocks a fresh native install that
        // mounts directly on /auth/register — the watchers above only fire on a
        // change, so the initial mount needs this explicit check.
        if (isNative && (loaded.value || isGuestRoute.value)) {
            await SplashScreen.hide();
        }
        if (refError.value) {
            showError(isProduction.value ? null : 'A reference error was encountered.');
        }
        await initAudio();
    });

    onBeforeUnmount(async () => {
        EventBus.off('error-fatal');
        EventBus.off('error-toast');
        EventBus.off('error-auth');
        EventBus.off('toasts-pause');
        EventBus.off('toasts-resume');
        EventBus.off('achievement-progress');
        EventBus.off('push-notification');
        EventBus.off(LIVE_UPDATE_DOWNLOADING_EVENT);
        EventBus.off(LIVE_UPDATE_PROGRESS_EVENT);
        EventBus.off(LIVE_UPDATE_DISMISS_EVENT);
        await SoundManager.stop(AUDIO_ASSET_ID);
        if (appStateListener) {
            await appStateListener.remove();
            appStateListener = null;
        }
        if (isNative) {
            // Release the keep-awake lock so it does not outlive the app.
            await KeepAwake.allowSleep();
            await SplashScreen.hide();
        }
    });
</script>

<style>
    .v-overlay__scrim {
        opacity: 80%;
        background-color: #000;
    }

    /* Full-screen blocking overlay shown while a live update downloads. Covers
       everything and captures all pointer events so the user cannot act on a
       half-updated app. It hands off to the raw-DOM "Updating..." reload overlay
       (z-index 999999) on apply, so this sits just below that. */
    .update-overlay {
        position: fixed;
        inset: 0;
        z-index: 99999;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #0E0228 url('/assets/update-splash.webp') center / cover no-repeat;
        text-align: center;
        padding: 24px;
    }

    .update-overlay__content {
        width: 100%;
        max-width: 320px;
        padding: 24px;
        border-radius: 16px;
        background-color: rgba(14, 2, 40, 0.82);
        box-shadow: 0 12px 36px rgba(0, 0, 0, 0.45);
        backdrop-filter: blur(4px);
        box-sizing: border-box;
    }

    .update-overlay__title {
        margin-top: 20px;
        color: #ffffff;
        font-size: 18px;
        font-weight: 600;
    }

    .update-overlay__subtitle {
        margin-top: 8px;
        color: rgba(255, 255, 255, 0.7);
        font-size: 14px;
        line-height: 1.4;
    }

    .update-overlay__percent {
        margin-top: 12px;
        color: #A8B8CC;
        font-size: 14px;
        font-variant-numeric: tabular-nums;
    }

    /* Use purpose-composed portrait artwork on narrow phones so the title and
       character remain visible without letterboxing. Tablets and wider screens
       retain the square edge-to-edge artwork above. */
    @media (max-width: 599px) and (orientation: portrait) {
        .update-overlay {
            background-image: url('/assets/update-splash-phone.webp');
            background-position: center top;
            background-size: cover;
        }
    }
</style>
